import { useState } from "react";
import { AlertTriangle, Info, Trash2 } from "lucide-react";

import { useServiceLabel } from "@/api/local/labels";
import { useRollouts } from "@/api/local/rollout";
import type { Pool, Replica, Service } from "@/api/types";
import { DeleteServiceModal } from "@/components/DeleteServiceModal";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";
import { GLOBAL_FOCUS, type Focus } from "@/workloads/focus";

interface Props {
  service: Service;
  pools: Pool[];
  replicas: Replica[];
  setFocusAndTab: (focus: Focus, tab: string) => void;
}

export function ServiceSettings({
  service,
  pools,
  replicas,
  setFocusAndTab,
}: Props) {
  const rollouts = useRollouts();
  const displayName = useServiceLabel(service.id, service.name);
  const [showDelete, setShowDelete] = useState(false);

  const draft = service.lifecycle_state === "draft";
  const rolloutInFlight =
    rollouts.active != null && rollouts.active.service_id === service.id;

  return (
    <>
      <Section title="Configuration" icon={<Info />}>
        <div className="kv">
          <span className="k">name</span>
          <span className="v mono">{service.name}</span>
          <span className="k">description</span>
          <span className="v">{service.description ?? "—"}</span>
          <span className="k">default_threshold_ms</span>
          <span className="v mono">{service.default_threshold_ms}</span>
          <span className="k">default_hash_key</span>
          <span className="v mono">{service.default_hash_key ?? "—"}</span>
          <span className="k">enabled</span>
          <span className="v">
            <Pill tone={service.enabled ? "ok" : "neutral"}>
              {service.enabled ? "enabled" : "disabled"}
            </Pill>
          </span>
          <span className="k">created_at</span>
          <span className="v mono muted">
            {new Date(service.created_at).toLocaleString()}
          </span>
          <span className="k">updated_at</span>
          <span className="v mono muted">
            {new Date(service.updated_at).toLocaleString()}
          </span>
        </div>
      </Section>

      {service.kind === "bank" ? (
        <Section title="Danger zone" icon={<AlertTriangle />}>
          <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            The capacity bank is a built-in singleton: BackStage seeds it on
            startup and there is exactly one, so it can&rsquo;t be renamed,
            disabled or deleted (the API refuses all three). To empty it,
            scale its pools to zero — the inventory drains and the pods are
            released, but the bank itself stays.
          </p>
        </Section>
      ) : (
      <Section
        title="Danger zone"
        icon={<AlertTriangle />}
        actions={
          <Pill tone="danger">irreversible</Pill>
        }
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {draft ? "Discard this draft service" : "Delete this service"}
            </div>
            <p
              className="muted"
              style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}
            >
              {draft ? (
                <>
                  This service has never been rolled out, so nothing is
                  running. Discarding removes the draft row, the workload
                  spec you&rsquo;ve been editing, and any pools you
                  attached.
                </>
              ) : (
                <>
                  Tears down every replica under <code>{service.name}</code>
                  , deletes all attached pools, and forgets routing rules
                  / schedules / rollout history. Cannot be undone.
                </>
              )}
            </p>
            {rolloutInFlight && (
              <div className="flash" style={{ marginTop: 12 }}>
                A rollout is currently in progress. Wait for it to finish
                or abort it before deleting the service.
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => setShowDelete(true)}
            disabled={rolloutInFlight}
            title={
              rolloutInFlight
                ? "Cannot delete while a rollout is in progress"
                : undefined
            }
          >
            <Trash2 />
            {draft ? "Discard draft…" : "Delete service…"}
          </button>
        </div>
      </Section>
      )}

      <DeleteServiceModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        service={service}
        displayName={displayName}
        isDraft={draft}
        pools={pools}
        replicas={replicas}
        onDeleted={() => setFocusAndTab(GLOBAL_FOCUS, "dashboard")}
      />
    </>
  );
}
